import { ChangeDetectorRef, Component } from '@angular/core';
import { SideMenuComponent } from '../../components/navbars/side-menu/side-menu.component';
import { NavbarComponent } from '../../components/navbars/navbar/navbar.component';
import { RouterOutlet } from '@angular/router';
import { DataService } from '../../Services/Data Service/data.service';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { catchError, filter, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { AuthService } from '../../Auth/auth.service';
import { CompanyTypesService } from '../../Services/Data Service/companytypes.service';
import { isAdminRole } from '../../roles';

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent, SideMenuComponent, NgIf, NgFor, CommonModule],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.css'
})
export class ProfilePageComponent {

  title = 'Your Profile';
  currUser: any;
  currCompany: any;
  loading: boolean = false;

  orgType = '';
  busTypes: any ;
  documents: any;
  hse_docs: any;
  documentList: any;

  isNavbarExpanded = false;
  orgOptions: any;

  businessTypeLabels : any;

  toggleCollapse(expanded: boolean) {
    this.isNavbarExpanded = expanded;
  }

  constructor(private dataService: DataService, private auth: AuthService, private cdr: ChangeDetectorRef, private typesDataService: CompanyTypesService) {}
  
  ngOnInit(): void {
    this.loading = true;

    this.dataService.currentUser$.pipe(
      filter((u): u is NonNullable<typeof u> => u != null),
      take(1),
      switchMap(user => {
        this.currUser = user;
        this.cdr.detectChanges();

        // Fetch company
        const baseRequests: any = {
          company: this.dataService.getCompanyByPK(user.company.intCompanyPK).pipe(
            switchMap(() => this.dataService.company$),
            take(1)
          ),
        };
      
        // Only add others if NOT Admin (Vendor)
        if (!isAdminRole(user.role)) {
          baseRequests.documents = this.dataService.getCompanyDocuments(user.company.intCompanyPK);
          baseRequests.hse_docs = this.dataService.getCompanyHSE(user.company.intCompanyPK);
          baseRequests.businessTypes = this.dataService.getCompanyBusinessTypes(user.company.intCompanyPK),
          baseRequests.businessTypeOptions = this.typesDataService.getBusinessTypes(),
          baseRequests.orgTypes = this.typesDataService.getOrgTypes()
        }
      
        return forkJoin(baseRequests) as Observable<{
          company: any;
          businessTypes?: any;
          businessTypeOptions?: any;
          orgTypes?: any;
          documents?: any;
          hse_docs: any;
        }>;
      }),
      catchError(err => {
        console.error('Error during initialization:', err);
        this.loading = false;
        return of(null);
      })
    ).subscribe(result => {
      if (!result) return;

      this.currCompany = result.company;
      this.documents = result.documents || {};

      this.hse_docs = Array.isArray(result.hse_docs) && result.hse_docs.length > 0 
        ? result.hse_docs[0] 
        : null;
      if (this.hse_docs != null && !('agreedToSustainabilityChecklist' in this.hse_docs)) {
        this.hse_docs.agreedToSustainabilityChecklist = false;
      }

      console.log(
        "Business Types:", result.businessTypes,
        "\nOrg Type FK (if any):", result.company.intCompanyOrgTypeFK,
        "\nAll Org Types to choose from:", result.orgTypes,
        "\nUnique Org Type (if that's the case):", result.company.strOtherOrgType
      );      

      //ORG TYPE: if there's no FK saved for intCompanyOrgTypeFK then the type enetered must have been unique (strOtherOrgType)
      const { businessTypeLabels, orgTypeLabel } = this.processCompanyTypes(
        result.businessTypes || [],
        result.businessTypeOptions || [],
        result.company.intCompanyOrgTypeFK,
        result.orgTypes || [],
        result.company.strOtherOrgType
      );
    
      this.businessTypeLabels = businessTypeLabels;
      this.orgType = orgTypeLabel;
    
      console.log("Org Type:", this.orgType);
      console.log("Business Types:", this.businessTypeLabels);


      this.documentList = [
        {
          label: 'VAT File',
          data: this.documents.vatFileData,
          path: this.documents.vatFilePath,
          expiry: this.documents.vatExpiryDate
        },
        {
          label: 'BIR File',
          data: this.documents.birFileData,
          path: this.documents.birFilePath
        },
        {
          label: 'NIB File',
          data: this.documents.nibFileData,
          path: this.documents.nibFilePath,
          expiry: this.documents.nibExpiryDate
        },
        {
          label: 'Incorporation File',
          data: this.documents.incorpFileData,
          path: this.documents.incorporationFilePath
        },
        {
          label: 'Registration File',
          data: this.documents.regFileData,
          path: this.documents.registrationFilePath
        },
        {
          label: 'Company Profile File',
          data: this.documents.companyFileData,
          path: this.documents.companyProfilePath
        },
        {
          label: 'Licenses File',
          data: this.documents.generalLicensesFileData,
          path: this.documents.generalLicensesPath,
          expiry: this.documents.generalLicensesExpiryDate
        },
        {
          label: 'Safety File',
          data: this.hse_docs?.safetyFileData,
          path: this.hse_docs?.safetyFilePath,
        },
        {
          label: 'Training File',
          data: this.hse_docs?.trainingFileData,
          path: this.hse_docs?.trainingFilePath,
        },
        {
          label: 'Resources File',
          data: this.hse_docs?.resourcesFileData,
          path: this.hse_docs?.resourcesFilePath,
        },
      ];
      
      this.cdr.detectChanges();
      console.log(this.documents);
      this.loading = false;
    });
  }

  processCompanyTypes(
    businessTypes: any[],
    businessTypeOptions: { label: string; value: string | number }[],
    orgTypeFK: number | string | null | undefined,
    orgTypeOptions: { label: string; value: string | number }[],
    otherOrgType: string
  ): { businessTypeLabels: string[]; orgTypeLabel: string } {
    const fullOrgTypeList = [...orgTypeOptions, { label: 'Other', value: 99 }];
    const fullBusinessTypeList = [...businessTypeOptions, { label: 'Other', value: 99 }];
  
    const businessTypeLabels = businessTypes.map(bt => {
      if (bt.intCompanyTypeFK === 99) return bt.strOtherType;
      const match = fullBusinessTypeList.find(opt => opt.value === bt.intCompanyTypeFK);
      return match ? match.label : bt.strOtherType || 'Unknown';
    });
  
    const orgTypeLabel =
      orgTypeFK === null || orgTypeFK === undefined || !fullOrgTypeList.some(opt => opt.value === orgTypeFK)
        ? otherOrgType
        : (fullOrgTypeList.find(opt => opt.value === orgTypeFK)?.label ?? 'Unknown');
  
    return { businessTypeLabels, orgTypeLabel };
  }
  
  
  
  
}